import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { fr, enUS } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const CustomIconLeft = () => <ChevronLeft className="h-4 w-4 text-slate-600" />;
const CustomIconRight = () => <ChevronRight className="h-4 w-4 text-slate-600" />;

const CustomDropdown = ({ value, onChange, children }: any) => (
  <div className="relative inline-flex items-center">
    <select
      value={value}
      onChange={onChange}
      className="cursor-pointer appearance-none bg-slate-50 hover:bg-slate-100/80 border border-slate-200/90 rounded-lg pl-2.5 pr-6 py-1 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all shadow-2xs"
    >
      {children}
    </select>
    <div className="pointer-events-none absolute right-1.5 flex items-center text-slate-400">
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  </div>
);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale: propLocale,
  ...props
}: CalendarProps) {
  const { i18n } = useTranslation();
  const activeLocale = propLocale || (i18n.language?.startsWith("fr") ? fr : enUS);

  return (
    <DayPicker
      locale={activeLocale}
      weekStartsOn={props.weekStartsOn ?? 1}
      showOutsideDays={showOutsideDays}
      className={cn("p-3.5 bg-white select-none", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-3.5",
        caption: "flex justify-center pt-1 pb-1 relative items-center min-h-[36px]",
        caption_label: "text-sm font-semibold text-slate-800",
        caption_dropdowns: "flex items-center gap-1.5 justify-center z-10",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-7 w-7 bg-transparent hover:bg-slate-100 text-slate-600 rounded-lg p-0 transition-colors"
        ),
        nav_button_previous: "absolute left-0 top-1/2 -translate-y-1/2",
        nav_button_next: "absolute right-0 top-1/2 -translate-y-1/2",
        table: "w-full border-collapse space-y-1",
        head_row: "flex w-full mb-1",
        head_cell:
          "text-slate-400 rounded-md flex-1 font-medium text-[0.75rem] text-center",
        row: "flex w-full mt-1.5",
        cell: "flex-1 flex items-center justify-center h-8 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-lg [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal text-xs text-slate-700 rounded-lg hover:bg-slate-100 transition-colors aria-selected:opacity-100 mx-auto"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-slate-900 text-white font-medium hover:bg-slate-900 hover:text-white focus:bg-slate-900 focus:text-white shadow-2xs",
        day_today:
          "font-semibold text-slate-900 border border-slate-300/80 bg-slate-50",
        day_outside:
          "day-outside text-slate-300 opacity-60 aria-selected:bg-slate-100/50 aria-selected:text-slate-400 aria-selected:opacity-30",
        day_disabled: "text-slate-300 opacity-40 hover:bg-transparent cursor-not-allowed",
        day_range_middle:
          "aria-selected:bg-slate-100 aria-selected:text-slate-900",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: CustomIconLeft,
        IconRight: CustomIconRight,
        Dropdown: CustomDropdown,
      }}
      {...(props.mode === "single" ? { required: true } : {}) as any}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
